import { useEffect, useRef, useState, KeyboardEvent, FocusEvent, ChangeEvent } from 'react';
import { clsx } from 'clsx';
import { Paper, Divider } from '@mui/material';
import { useDontMountAtFirst } from '../../shared/hooks/useDontMountAtFirst';
import { useInput } from '../hooks/useInput';
import { useDebounce } from '../../shared/hooks/useDebounce';
import { Icon } from '../../icon/Icon';
import { IconButton } from '../../button/IconButton/IconButton';
import styles from './SearchArea.module.scss';
import { getDataIdGenerator } from '../../shared/utils/dataId';
import { InputBase } from '../InputBase/InputBase';
import { Button } from '../../button/Button';
import { DEFAULT_STRING_OPERATOR, STRING_OPERATORS } from '../../filter/constants';
import { SearchValueType } from './interface';
import { InlineAutocompleteWithExternalTrigger } from '../InlineAutoComplete/InlineAutocompleteWithExternalTrigger';

export interface SearchProps {
  dataId: string;
  placeholder?: string;
  autofocus?: boolean;
  fullWidth?: boolean;
  hasBorder?: boolean;
  iconSize?: number;
  className?: string;
  filled?: boolean;
  wrapperClassName?: string;
  disabled?: boolean;
  defaultExpanded?: boolean;
  debounceInterval?: number;
  collapsible?: boolean;
  defaultSearch?: string;
  onChange?: (arg0: SearchValueType) => void;
  onSearch: (arg0: SearchValueType) => void;
  onCollapsedStateChange?: (state: boolean) => void;
  keyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  currentValue?: string;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onRefUpdate?: (ref: HTMLInputElement | null) => void;
  searchWrapperClassName?: string;
  maxWidth?: string;
  showOperators?: boolean;
}

export function SearchArea({
  dataId,
  placeholder,
  autofocus,
  fullWidth = false,
  hasBorder = true,
  iconSize = 2,
  className,
  filled,
  wrapperClassName = '',
  disabled,
  defaultExpanded = true,
  onSearch,
  debounceInterval = 300,
  collapsible = true,
  defaultSearch = '',
  onChange,
  onCollapsedStateChange,
  keyDown,
  currentValue = '',
  onBlur,
  onFocus,
  onRefUpdate,
  searchWrapperClassName = '',
  maxWidth,
  showOperators = true
}: SearchProps) {
  const [expanded, setExpanded] = useState(collapsible ? defaultExpanded : true);
  const [val, setVal] = useInput(defaultSearch);
  const debouncedSearchTerm = useDebounce(val, debounceInterval);
  const [openOperatorsPopper, setOpenOperatorsPopper] = useState(false);
  const [operator, setOperator] = useState(DEFAULT_STRING_OPERATOR);

  const inputRef = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (onRefUpdate) {
      onRefUpdate(inputRef.current);
    }
  }, []);

  useEffect(() => {
    if (currentValue !== val && currentValue?.length) {
      setVal(currentValue, false);
    }
  }, [currentValue]);

  useEffect(() => {
    if (onChange) {
      onChange(showOperators ? { operator: operator.value, value: val } : val);
    }
  }, [val, operator]);

  useDontMountAtFirst(() => {
    if (onSearch) {
      onSearch(showOperators ? { operator: operator.value, value: val } : val);
    }
  }, [debouncedSearchTerm, operator]);

  useDontMountAtFirst(() => {
    if (expanded && inputRef.current) {
      inputRef.current.focus();
    }

    if (onCollapsedStateChange) {
      onCollapsedStateChange(!!expanded);
    }
  }, [expanded]);

  const toggleExpand = () => {
    setExpanded(prev => !prev);
  };

  const clearSearch = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    setVal('', true);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.keyCode === 27 && !val && expanded && collapsible) {
      setExpanded(false);
      return;
    }

    if (keyDown) {
      keyDown(e);
    }
  };

  const onOperatorChange = (value: { label: string; value: string }) => {
    setOperator(value);
    setOpenOperatorsPopper(false);
  };

  const dataIdGenerator = getDataIdGenerator(dataId, 'search');

  return (
    <div
      className={clsx(
        styles.searchWrapper,
        collapsible && expanded && styles.collapsible,
        expanded && fullWidth && styles.fullWidth,
        !hasBorder && styles.noBorder,
        filled && styles.filled,
        searchWrapperClassName
      )}
      style={maxWidth ? { maxWidth } : undefined}
      data-id={dataIdGenerator('wrapper')}
    >
      {expanded ? (
        <Paper
          className={clsx(
            styles.searchBox,
            wrapperClassName,
            disabled && styles.disabled,
            showOperators && styles.searchBoxWithOperators
          )}
          sx={{ color: 'inherit' }}
          data-id='search-box'
        >
          <Icon name='search' size={iconSize} className={styles.icon} />

          <InputBase
            inputProps={{
              'aria-label': placeholder,
              'data-id': dataIdGenerator('input')
            }}
            inputRef={inputRef}
            className={clsx(className, styles.searchInputBase)}
            autoFocus={autofocus}
            placeholder={placeholder}
            value={val}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setVal(e)}
            onKeyDown={e => onKeyDown(e)}
            disabled={disabled}
            onBlur={e => onBlur && onBlur(e)}
            onFocus={e => onFocus && onFocus(e)}
          />

          {(val && val.length > 0) || collapsible === true ? (
            <IconButton
              aria-label='search'
              type='button'
              label='Clear Search'
              onClick={clearSearch}
              className={clsx(styles.actionButton)}
              dataId={dataIdGenerator('clear')}
            >
              <Icon name='close' size={2} className='text-secondary' />
            </IconButton>
          ) : null}

          {showOperators ? (
            <>
              <Button
                className={clsx('ml-2')}
                id={dataIdGenerator('operators')}
                dataId={dataIdGenerator('operators')}
                variation='faded'
                size='sm'
                onClick={() => setOpenOperatorsPopper(prev => !prev)}
              >
                <p>{operator.label}</p>
                <Divider
                  orientation='vertical'
                  variant='fullWidth'
                  flexItem
                  className='mx-1 bg-primary-faded'
                />
                <Icon name={openOperatorsPopper ? 'up-arrow' : 'dropdown-arrow'} />
              </Button>

              <InlineAutocompleteWithExternalTrigger
                value={operator}
                close={() => setOpenOperatorsPopper(false)}
                target={`#${dataIdGenerator('operators')}`}
                dataId={dataIdGenerator('operators')}
                open={openOperatorsPopper}
                options={STRING_OPERATORS}
                selectMode
                onChange={value => {
                  onOperatorChange(value as { label: string; value: string });
                }}
              />
            </>
          ) : null}
        </Paper>
      ) : (
        <IconButton
          aria-label='search'
          type='button'
          label='Expand'
          onClick={toggleExpand}
          className={styles.actionButton}
          dataId={dataIdGenerator('toggle')}
        >
          <Icon name='search' size={3} />
        </IconButton>
      )}
    </div>
  );
}
